import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Button } from "@/components/ui/button";
import { ArrowRight, Info, ChevronLeft, ChevronRight } from "lucide-react";

const products = [
  {
    id: "livestock-shelters",
    name: "4-5m Livestock Shelter",
    description: "Our standard-sized heavy-duty steel frame shelter, built for Australian conditions. Protect your livestock from sun, wind, and rain with our proven 4-5 metre design.",
    features: [
      "Standard 4-5 metre span",
      "Heavy-duty steel frame",
      "Optional kick boards available",
      "Installation + delivery available"
    ],
    price: "From $4,500 + GST",
    priceNote: "Kit price",
    images: ["/IMG_6286_shelter_main.jpeg", "/IMG_6296.jpeg", "/shelter_normal.jpg", "/shelter_new1.jpg", "/shelter_new2.jpg"],
    hasQuoteCalculator: true
  },
  {
    id: "custom-sheds",
    name: "Custom Sheds",
    description: "Have a specific vision for your shed? We'll work with you to design and build a custom shed tailored to your exact requirements. From size and layout to materials and features - we bring your ideas to life.",
    features: [
      "Fully customised design",
      "Built to your specifications",
      "Quality steel construction",
      "Free consultation & quote"
    ],
    price: "Price on Request",
    priceNote: "Contact for quote",
    images: ["/sea_container_6.jpg", "/sea_container_transparent_roof.jpg", "/sea_container_5.jpg", "/livestock_shelter_yards.jpg", "/livestock_shelter_green.jpg"]
  },
  {
    id: "sea-container-lean-to",
    name: "Sea Container Lean-To",
    description: "Custom lean-to shelters built off shipping containers. Available in 20ft and 40ft configurations. Perfect for workshops, storage, or machinery cover.",
    features: [
      "20ft or 40ft container options",
      "Custom lean-to design",
      "Heavy-duty construction",
      "Various configurations available"
    ],
    price: "Price on Request",
    images: ["/sea_container_1.jpg", "/sea_container_2.jpg", "/sea_container_3.jpg", "/sea_container_4.jpg"]
  },
  {
    id: "stick-rakes-attachments",
    name: "Stick Rakes & Custom Attachments",
    description: "Heavy-duty stick rakes and loader buckets for clearing land, debris, and material handling. Available in multiple sizes to suit your machinery.",
    features: [
      "Stick rakes (top seller!)",
      "Loader buckets (various sizes)",
      "For wheel loaders, telehandlers & skid steers",
      "Tractor compatible",
      "Heavy-duty construction"
    ],
    price: "From $4,500",
    images: ["/IMG_6280_stickrake_main.jpg", "/IMG_6309_stickrake.jpg", "/IMG_6311_bucket.jpg"]
  },
  {
    id: "ute-tray-builds",
    name: "Ute Tray Builds",
    description: "Custom heavy-duty ute trays built to your specifications. Available for single cab, dual cab, and space cab configurations.",
    features: [
      "Single cab options",
      "Dual cab options",
      "Space cab options",
      "Built to your specs"
    ],
    price: "Price on Request",
    images: ["/IMG_6310_utetray.jpg", "/IMG_6283_utetray.jpg", "/IMG_6290_utetray.jpg"]
  },
  {
    id: "cattle-yards",
    name: "Cattle Yards & Gates",
    description: "Robust cattle yards, gates, and panels designed for easy handling and long-lasting durability.",
    features: [
      "Heavy-duty construction",
      "Custom configurations",
      "Loading ramps available",
      "Gates and panels"
    ],
    price: "Price on Request",
    images: ["/IMG_6303.jpeg"]
  },
  {
    id: "bespoke-projects",
    name: "Bespoke Projects",
    description: "One-off custom fabrication jobs. From toolboxes to benches to hay feeders, we can build whatever you need.",
    features: [
      "Custom toolboxes",
      "Memorial benches",
      "Hay feeders",
      "One-off custom jobs"
    ],
    price: "Price on Request",
    images: ["/IMG_6291_toolbox.jpg", "/IMG_6295_bench.jpg", "/IMG_6282_hayfeeder.jpg"]
  }
];

// Clean Professional Image Carousel with smooth slide animation
function ProductImageCarousel({ images, name }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const containerRef = useRef(null);
  const touchDataRef = useRef({ startX: 0, startY: 0, endX: 0, isSwiping: false });
  const currentIndexRef = useRef(currentIndex);
  const isTransitioningRef = useRef(isTransitioning);

  // Keep refs in sync with state
  useEffect(() => {
    currentIndexRef.current = currentIndex;
  }, [currentIndex]);

  useEffect(() => {
    isTransitioningRef.current = isTransitioning;
  }, [isTransitioning]);

  const minSwipeDistance = 30;

  const goToImage = (index) => {
    if (isTransitioningRef.current) return;
    setIsTransitioning(true);
    isTransitioningRef.current = true;
    setCurrentIndex(index);
    currentIndexRef.current = index;
    setTimeout(() => {
      setIsTransitioning(false);
      isTransitioningRef.current = false;
    }, 300);
  };

  const nextImage = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (currentIndexRef.current < images.length - 1) {
      goToImage(currentIndexRef.current + 1);
    }
  };

  const prevImage = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (currentIndexRef.current > 0) {
      goToImage(currentIndexRef.current - 1);
    }
  };

  // Use native event listeners for touch to allow preventDefault
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleTouchStart = (e) => {
      touchDataRef.current = {
        startX: e.touches[0].clientX,
        startY: e.touches[0].clientY,
        endX: e.touches[0].clientX,
        isSwiping: false
      };
    };

    const handleTouchMove = (e) => {
      const { startX, startY } = touchDataRef.current;
      const currentX = e.touches[0].clientX;
      const currentY = e.touches[0].clientY;
      const diffX = Math.abs(startX - currentX);
      const diffY = Math.abs(startY - currentY);

      // If horizontal movement is greater than vertical, prevent page scroll
      if (diffX > diffY && diffX > 10) {
        e.preventDefault();
        touchDataRef.current.isSwiping = true;
      }
      touchDataRef.current.endX = currentX;
    };

    const handleTouchEnd = () => {
      const { startX, endX } = touchDataRef.current;
      const distance = startX - endX;
      
      if (Math.abs(distance) > minSwipeDistance && images.length > 1) {
        if (distance > 0 && currentIndexRef.current < images.length - 1) {
          goToImage(currentIndexRef.current + 1);
        } else if (distance < 0 && currentIndexRef.current > 0) {
          goToImage(currentIndexRef.current - 1);
        }
      }
      touchDataRef.current.isSwiping = false;
    };

    container.addEventListener('touchstart', handleTouchStart, { passive: true });
    container.addEventListener('touchmove', handleTouchMove, { passive: false });
    container.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('touchmove', handleTouchMove);
      container.removeEventListener('touchend', handleTouchEnd);
    };
  }, [images.length]); // Only re-attach if images array length changes

  if (!images || images.length === 0) {
    return (
      <div className="aspect-[4/3] overflow-hidden rounded-t-[1.5rem] bg-[#1A1A1A] flex items-center justify-center">
        <div className="text-center p-6">
          <div className="w-16 h-16 bg-[#E63946]/10 border border-[#E63946]/30 rounded-xl mx-auto mb-4 flex items-center justify-center">
            <Info className="w-8 h-8 text-[#E63946]" />
          </div>
          <p className="text-[#A3A3A3] text-sm">Contact for details</p>
        </div>
      </div>
    );
  }

  return (
    <div 
      ref={containerRef}
      className="aspect-[4/3] overflow-hidden rounded-t-[1.5rem] relative group bg-[#0A0A0A]"
      style={{ touchAction: 'pan-y' }}
    >
      {/* Images Container - Horizontal slide */}
      <div 
        className="flex h-full transition-transform duration-300 ease-out"
        style={{ transform: `translateX(-${currentIndex * 100}%)` }}
      >
        {images.map((img, idx) => (
          <img 
            key={idx}
            src={img}
            alt={`${name} - Image ${idx + 1}`}
            className="w-full h-full object-cover flex-shrink-0 select-none pointer-events-none"
            draggable="false"
          />
        ))}
      </div>
      
      {/* Swipe hint for mobile - only show if multiple images */}
      {images.length > 1 && (
        <div className="absolute top-3 right-3 md:hidden">
          <div className="px-3 py-1.5 bg-black/70 backdrop-blur-sm rounded-full shadow-lg border border-white/10">
            <span className="text-white text-xs font-bold uppercase tracking-wider">Swipe</span>
          </div>
        </div>
      )}
      
      {/* Gradient overlay for better UI visibility */}
      <div className="absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-black/60 to-transparent pointer-events-none" />
      
      {/* Navigation Controls */}
      {images.length > 1 && (
        <>
          {/* Left Arrow - Desktop only */}
          <button
            onClick={prevImage}
            className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/10 backdrop-blur-sm hover:bg-white/20 rounded-full hidden md:flex items-center justify-center transition-all duration-200 opacity-0 group-hover:opacity-100 border border-white/20"
            aria-label="Previous image"
          >
            <ChevronLeft className="w-5 h-5 text-white" />
          </button>
          
          {/* Right Arrow - Desktop only */}
          <button
            onClick={nextImage}
            className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/10 backdrop-blur-sm hover:bg-white/20 rounded-full hidden md:flex items-center justify-center transition-all duration-200 opacity-0 group-hover:opacity-100 border border-white/20"
            aria-label="Next image"
          >
            <ChevronRight className="w-5 h-5 text-white" />
          </button>
          
          {/* Image Counter & Progress Bar */}
          <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between">
            {/* Counter Badge */}
            <span className="px-3 py-1 bg-black/50 backdrop-blur-sm rounded-full text-white text-xs font-medium">
              {currentIndex + 1} / {images.length}
            </span>
            
            {/* Progress Dots */}
            <div className="flex gap-1.5">
              {images.map((_, idx) => (
                <button
                  key={idx}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    goToImage(idx);
                  }}
                  className={`h-1.5 rounded-full transition-all duration-200 ${
                    idx === currentIndex 
                      ? 'bg-[#E63946] w-6' 
                      : 'bg-white/40 w-1.5 hover:bg-white/70'
                  }`}
                  aria-label={`Go to image ${idx + 1}`}
                />
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default function ProductsPage() {
  const location = useLocation();
  const containerRef = useRef(null);

  // Scroll to section if hash is present
  useEffect(() => {
    if (location.hash) {
      const element = document.getElementById(location.hash.slice(1));
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 100);
      }
    }
  }, [location.hash]);

  // Scroll animation observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("visible");
          }
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -50px 0px" }
    );

    const elements = containerRef.current?.querySelectorAll(".animate-on-scroll");
    elements?.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  return (
    <div className="page-transition pt-20" data-testid="products-page" ref={containerRef}>
      <Helmet>
        <title>Horse Shelters & Stick Rakes for Sale | PWC Fabrication Albany & Great Southern WA</title>
        <meta name="description" content="Buy quality horse shelters from $4,500, stick rakes from $6,500 at PWC Fabrication Albany. Livestock shelters, loader buckets & custom ute trays. Delivery to Great Southern Western Australia." />
        <meta name="keywords" content="horse shelters for sale Albany, buy horse shelter Great Southern, stick rakes for sale, stick rake attachments WA, livestock shelters Albany, loader buckets, ute tray builds, cattle yards, PWC products Albany" />
        <link rel="canonical" href="https://pwcengineering.com.au/products" />
      </Helmet>
      
      {/* Hero Section */}
      <section className="py-20 md:py-32 bg-[#121212]" data-testid="products-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="inline-block text-[#E63946] font-semibold uppercase tracking-widest mb-4 text-sm">
              Our Products
            </span>
            <h1 className="text-5xl md:text-6xl font-bold tracking-tight uppercase text-white mb-6">
              Quality Equipment
            </h1>
            <p className="text-xl text-[#A3A3A3] leading-relaxed mb-6">
              Heavy-duty products manufactured right here in Australia. Built tough for 
              Australian conditions and designed to last.
            </p>
            
            {/* Custom Order Notice */}
            <div className="flex items-start gap-4 p-6 bg-[#1A1A1A] border border-[#E63946]/30 rounded-2xl">
              <Info className="text-[#E63946] flex-shrink-0 mt-1" size={24} />
              <div>
                <p className="text-white font-semibold mb-2">Custom Orders Only</p>
                <p className="text-[#A3A3A3]">
                  All products can be customised to suit your equipment and requirements. 
                  Contact us for an accurate quote tailored to your needs.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Products Grid */}
      <section className="py-20 md:py-32 bg-[#0A0A0A]" data-testid="products-grid">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {products.map((product, index) => (
              <div 
                key={product.id}
                id={product.id}
                className={`product-card bg-[#121212] border border-[#2A2A2A] overflow-hidden animate-on-scroll delay-${(index % 3 + 1) * 100}`}
                data-testid={`product-card-${product.id}`}
              >
                {/* Product Image Carousel */}
                <ProductImageCarousel images={product.images} name={product.name} />

                {/* Product Info */}
                <div className="p-6">
                  <h3 className="text-2xl font-bold uppercase tracking-wide text-white mb-3">
                    {product.name}
                  </h3>
                  <p className="text-[#A3A3A3] mb-4">
                    {product.description}
                  </p>

                  {/* Features List */}
                  <ul className="space-y-2 mb-6">
                    {product.features.slice(0, 3).map((feature, i) => (
                      <li key={i} className="text-sm text-[#A3A3A3] flex items-center gap-2">
                        <span className="w-2 h-2 bg-[#E63946] rounded-full" />
                        {feature}
                      </li>
                    ))}
                  </ul>

                  {/* Link to dedicated page for Livestock Shelter */}
                  {product.hasQuoteCalculator && (
                    <div className="pt-4 border-t border-[#2A2A2A]">
                      <div className="mb-4">
                        <p className="text-sm text-[#525252] uppercase tracking-wider">Starting</p>
                        <p className="text-2xl font-bold text-[#E63946]">{product.price}</p>
                      </div>
                      <Link to="/products/livestock-shelter">
                        <Button 
                          className="w-full bg-[#E63946] hover:bg-[#D62839] text-white font-bold uppercase tracking-wider py-3 rounded-full btn-glow"
                          data-testid={`${product.id}-quote-btn`}
                        >
                          Build Your Quote
                          <ArrowRight className="ml-2 w-4 h-4" />
                        </Button>
                      </Link>
                    </div>
                  )}

                  {/* Price - for other products */}
                  {!product.hasQuoteCalculator && (
                    <div className="flex items-center justify-between pt-4 border-t border-[#2A2A2A]">
                      <div>
                        <p className="text-sm text-[#525252] uppercase tracking-wider">Starting</p>
                        <p className="text-2xl font-bold text-[#E63946]">{product.price}</p>
                      </div>
                      <Link to={`/contact?product=${encodeURIComponent(product.name)}`}>
                        <Button 
                          className="bg-[#E63946] hover:bg-[#D62839] text-white font-bold uppercase tracking-wider px-4 py-2 text-sm rounded-full btn-glow"
                          data-testid={`${product.id}-quote-btn`}
                        >
                          Request Quote
                        </Button>
                      </Link>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Custom Build CTA */}
      <section className="py-20 md:py-32 bg-[#E63946] relative overflow-hidden" data-testid="products-cta">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-black/10 rounded-full -translate-y-1/2 translate-x-1/4" />
        <div className="absolute bottom-0 left-0 w-[300px] h-[300px] bg-black/10 rounded-full translate-y-1/3 -translate-x-1/4" />
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold tracking-tight uppercase text-white mb-6">
            Need Something Custom Built?
          </h2>
          <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            PWC Fabrication & Engineering specialises in custom fabrication and engineered 
            solutions. If you can't find what you need, we'll design and build it for you.
          </p>
          <Link to="/contact">
            <Button 
              className="bg-white hover:bg-gray-100 text-[#E63946] font-bold uppercase tracking-wider px-8 py-6 text-lg rounded-full shadow-xl"
              data-testid="products-cta-btn"
            >
              Request a Quote
              <ArrowRight className="ml-2" size={20} />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
